import { Component, OnInit, EventEmitter, Input, Output, HostListener, OnDestroy, trigger, state, transition, animate, style } from '@angular/core';
import { AccordionConfig } from '../../models/accordion-config';
import { ResourceService } from 'sce-core';
import { DetailViewKeyEventHandlerService } from '../../services/detail-view-key-event-handler.service';
import { SearchableEntityDataService } from '../../services/searchable-entity/searchable-entity-data.service';
import { SearchableEntityDataEditorService } from '../../services/searchable-entity/searchable-entity-data-editor.service';
import { NavigationService } from '../../../core/navigation.service';
import { LeftPanelService } from '../../../layout/home/services/left-panel.service';
import { AlertService } from 'sce-core';
import { UserService } from '../../../core/security/user.service';
import { PropertyChangeEvent, MANUAL_EDIT } from '../../models/property-change-event';
import { Subscription } from 'rxjs/Subscription';
import { DatePickerConfigService } from '../datepicker/datepicker-config.service';
import { DetailedEntityDataService } from '../../services/searchable-entity/detailed-entity/detailed-entity-data.service';
import { NEW_SEARCH_EVENT } from '../../search/search-constants';
import { stringify } from 'querystring';

@Component({
  selector: 'lfwms-accordion',
  templateUrl: './accordion.component.html',
  styleUrls: ['./accordion.component.css'],
  animations: [
    trigger('tabContent', [
      state('hidden', style({
        height: '0'
      })),
      state('visible', style({
        height: '*'
      })),
      transition('visible <=> hidden', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
    ])
  ]
})
export class AccordionComponent implements OnInit, OnDestroy {

  @Input() accordionConfig: AccordionConfig;
  @Output() reorder: EventEmitter<any> = new EventEmitter();

  public metadataMap: any;
  public cellDataMap: any;
  public propertyList: any;
  public accordionTitle: string;
  public isLoading = false;
  public isPrimaryDataLoaded = false;
  public panel: any;
  public default: any;
  private dataService: SearchableEntityDataService;
  private dataEditor: SearchableEntityDataEditorService;
  private keyEventHandler: DetailViewKeyEventHandlerService;
  public windowWidth: any;
  private sidePanelWidth: number;
  public searchValue: any;
  private eventSubscriptions: Subscription = new Subscription();
  public dateFormat: string;
  public dateTimeFormat: string;
  origDetailsMetaDataMap: any;

  public isAccordionLoading: boolean = true;
  public isAccordionAnimating: boolean= false;

  // Method to recalculate cell width and align Property Grid on Window Resize event
  @HostListener('window:resize', ['$event'])
  public onResize($event: any) {
    this.windowWidth = $event.target.innerWidth;
    this.realignPropertyGrid();
  }

  constructor(protected resourceService: ResourceService,
    protected navService: NavigationService,
    protected leftPanelService: LeftPanelService,
    protected alertService: AlertService,
    protected userService: UserService,
    protected dateConfigService: DatePickerConfigService) {
  }

  public ngOnInit() {
    this.metadataMap = this.accordionConfig.metaDataMap;
    this.cellDataMap = this.accordionConfig.cellDataMap;
    /* set initial value before first call to realignPropertyGrid()
    or  any events are received via subscription.edge case. */
    this.sidePanelWidth = this.leftPanelService.isOpened() ? this.leftPanelService.getLeftPanelWidthInPixels() : 0;
    this.realignPropertyGrid();
    this.leftPanelService.getLeftPanelOpenEvent().subscribe((isOpened) => {
      if (isOpened) {
        this.sidePanelWidth = this.leftPanelService.getLeftPanelWidthInPixels();
      } else {
        this.sidePanelWidth = 0;
      }
      this.realignPropertyGrid();
    });
    this.accordionTitle = this.accordionConfig.name;
    if (this.accordionConfig.isPrimaryAccordion) {
      this.accordionConfig.isOpen = true;
    }
    this.dataService = this.accordionConfig.dataService;
    this.dataEditor = this.accordionConfig.dataEditor;
    this.keyEventHandler = this.accordionConfig.keyEventHandler;
    this.dataService.originalDataMap = JSON.parse(JSON.stringify(this.accordionConfig.cellDataMap));
    this.dateFormat = this.dateConfigService.defaultDateDisplayFormat;
    this.dateTimeFormat = this.dateConfigService.defaultDateDisplayFormat + ' ' + this.dateConfigService.defaultTimeDisplayFormat;
    // [LFWM-2346] Dropdown values should be filtered if the screen contain storer key field
    if (this.cellDataMap['storerKey'] || this.cellDataMap['fromStorerKey']) {
      /* [LFWM-2923] - to filter all the dropdown values when changing the storer value in accordion and table
      when loading the page itself */
      this.dataEditor.populateDropdownValuesBasedOnStorerInAccordion();
      this.dataEditor.populateDropdownValuesBasedOnStorerInTables();
    }else {
      /*[LFWM - 2923] - To filter the dropdown values based on filter
      which don't have StorerKey in meta data
      From User info fetch the storer restictions if any and do the filtering */
      let storerRestriction: any;
      let restrictionsArray: any;
      Object.keys(this.userService.userInfo.userRestrictions[0]).forEach(key => {
        if (key === 'restrictions') {
          restrictionsArray = this.userService.userInfo.userRestrictions[0][key];
          storerRestriction = restrictionsArray[1];
        }
      });
      Object.keys(storerRestriction).forEach(key => {
        if (key === 'restrictionValue' && storerRestriction[key] === null) {
          return;
        } else if (key === 'restrictionValue' && storerRestriction[key] !== null) {
          const storerRestrictionArray = storerRestriction[key].split(',');
           this.dataEditor.populateDropdownValuesBasedOnDefaultStorer(storerRestrictionArray);
        }
      });
    }
    this.subscriptionFunction();
  }


  public realignPropertyGrid() {
    let cellsPerRow: number = 0;
    this.windowWidth = window.innerWidth - this.sidePanelWidth;

    switch (true) {
      case (this.windowWidth > 1250): {
        cellsPerRow = 5;
        break;
      }
      case (this.windowWidth <= 1250 && this.windowWidth > 1030): {
        cellsPerRow = 4;
        break;
      }
      case (this.windowWidth <= 1030 && this.windowWidth >= 800): {
        cellsPerRow = 3;
        break;
      }
      case (this.windowWidth <= 800 && this.windowWidth >= 560): {
        cellsPerRow = 2;
        break;
      }
      case (this.windowWidth < 560): {
        cellsPerRow = 1;
        break;
      }
      default: {
        cellsPerRow = 5;
        break;
      }
    }
    const newPropertyList = this.removeHiddenFiedls();
    this.propertyList = this.preparePropertyGrid(newPropertyList, cellsPerRow);
  }

  public removeHiddenFiedls() {
    const propertyListArray = this.accordionConfig.propertyList;
    const newPropertyListArray = [];
    for (let i = 0; i < propertyListArray.length; i++) {
      if (this.metadataMap[propertyListArray[i]]['displayType'] !== 'H') {
        newPropertyListArray.push(propertyListArray[i]);
      }
    }
    return newPropertyListArray;
  }
  // Method to define grid layout for each accordion.
  private preparePropertyGrid(propertyList: Array<any>, cellsPerRow) {
    let accordionRow;
    const accordionDefinition = [];
    for (let x = 0; x < propertyList.length; x++) {
      if (x % cellsPerRow === 0) {
        if (accordionRow && accordionRow.length === cellsPerRow) {
          accordionDefinition.push(accordionRow);
        }
        accordionRow = [];
      }
      accordionRow.push(propertyList[x]);
    }
    if (accordionRow && accordionRow.length !== 0) {
      for (let y = accordionRow.length; y < cellsPerRow; y++) {
        accordionRow.push('dummy');
      }
      accordionDefinition.push(accordionRow);
    }
    return accordionDefinition;
  }

  // method used to translate the labels
  public getLabel(key: any) {
    return this.resourceService.get(key);
  }

  // Method to return Cell Title corresponding to each property
  public getCellTitle(property: any): string {
    return this.resourceService.get(this.metadataMap[property].elementId);
  }

  // Method  to override default display format for each cell value (in VIEW Mode ONLY)
  public getCellDisplayValue(property: any): any {
    if (!this.isLoading) {
      let cellDisplayValue: any;
      const cellDefinition: any = this.cellDataMap[property];
      const cellMetadata: any = this.metadataMap[property];
      if (property === 'insuredAmount' || property === 'carrierCharges' || property === 'otherCharges') {
        let decimalValue = this.cellDataMap[property].cVal;
        let afterDecimalValue = decimalValue.toString().split(".")[1];
        if (afterDecimalValue === '0') {
          this.cellDataMap[property].cVal =decimalValue.toString().split(".")[0];
        }
      }
      if (cellDefinition && !cellDefinition.cVal) {
        if (property === 'qty' && cellMetadata.defaultVal !== '') {
          cellDisplayValue = cellMetadata.defaultVal;
        } else {
          cellDisplayValue = '';
        }
      } else {
        if (cellMetadata.type === 'DROPDOWN' || cellMetadata.type === 'EDITABLEDROPDOWN') {
          const filteredValueObject = cellMetadata.values.filter((valueObject) => {
            // For string type dropdowns case insensitive comparison is done
            if(valueObject.key && cellDefinition.cVal && typeof (valueObject.key) === 'string' && typeof (cellDefinition.cVal) === 'string') {
              return (valueObject.key.toUpperCase() === cellDefinition.cVal.toUpperCase());
            } else {
              return (valueObject.key === cellDefinition.cVal);
            }
          });
          if (filteredValueObject.length > 0) {
             // LFWM-2709 - display only key in view mode
             //LFWM-3357- display value of dropdown in view mode
            // if (property === 'facility') {
               cellDisplayValue = filteredValueObject[0].value;
            // } 
            // else {
            //   cellDisplayValue = filteredValueObject[0].value;
            // }
          } else {
            /* [LFWM-2114] -if type - EDITABLEDROPDOWN - In View mode the input value of dropdown is not present in dropdown list then the input value
            from loadbypage is assigned to cellDisplayValue */
            // If cell value not in dropdown show that value as such
            cellDisplayValue = cellDefinition.cVal;
          }
        } else if (cellMetadata.type === 'DATE' || cellMetadata.type === 'DATETIME') {
          if (cellDefinition.cVal) {
            //  cellDisplayValue = moment(cellDefinition.cVal).format('MM/DD/YYYY');
            if (cellMetadata.type === 'DATE') {
              cellDisplayValue = this.dateConfigService.getFormattedDisplayDate(cellDefinition.cVal);
            } else if (cellMetadata.type === 'DATETIME') {
              cellDisplayValue = this.dateConfigService.getFormattedDisplayDateTime(cellDefinition.cVal);
            }
          } else {
            cellDisplayValue = '';
          }
        } else {
          if (cellDefinition && cellDefinition.cVal) {
            cellDisplayValue = cellDefinition.cVal;
          }
        }
      }
      return cellDisplayValue;
    } else {
      return '';
    }
  }

  public toggleAccordionExpand() {
    this.accordionConfig.isOpen = !this.accordionConfig.isOpen;
  }

  public emitReorderEvent(isDrop: any, columnGroup: string) {
    this.reorder.emit({ isDrop: isDrop, columnGroup: columnGroup });
  }

  public valueChanged(key: any, sourceEvent?: any) {
    let newVal = null;
    if (sourceEvent !== null && sourceEvent !== undefined) {
      if ('string' === typeof sourceEvent) {
        newVal = sourceEvent;
      } else if ('object' === typeof sourceEvent) {
        newVal = sourceEvent['newVal'];
      }
    }

    if ((newVal === null || newVal === undefined) && this.metadataMap[key].type !== 'DATE' && this.metadataMap[key].type !== 'DATETIME') {
      newVal = this.cellDataMap[key].cVal;
    }

    // if the value has changed, send an event and check if the change can be accepted
    const event: PropertyChangeEvent = new PropertyChangeEvent();
    event.source = 'table-editor';
    event.type = MANUAL_EDIT;
    event.property = this.cellDataMap[key].cName;
    event.value = newVal;
    // JIRA LFWM-1474 Fixes - current value and previous value of a dropdown element should not be the same value
    if (sourceEvent && sourceEvent.type && sourceEvent.type === 'dropdown') {
      event.previousValue = sourceEvent.prevVal;
    } else {
      event.previousValue = this.cellDataMap[key].cVal;
    }
    const acceptChange: boolean = this.dataEditor.propertyValueChanged(event);
    // if the property change is to be rejected, do not set the is edited flag
    if (acceptChange) {
      const hasValueChanged = (newVal !== this.dataService.originalDataMap[key].cVal);
      this.cellDataMap[key].isEdited = hasValueChanged;
      this.navService.preventNavigation = hasValueChanged;
      // if the event is from dropdown the current value is already set in this.cellDataMap[key].cVal
      if (sourceEvent && sourceEvent.type && sourceEvent.type === 'dropdown') {
        this.cellDataMap[key].cValPrev = sourceEvent.prevVal;
      } else {
        this.cellDataMap[key].cValPrev = this.cellDataMap[key].cVal;
        this.cellDataMap[key].cVal = newVal;
      }
      // [LFWM-2923] - filtering the dropdown values based on storer when storer key value changed
      if (key === 'storerKey' || key === 'fromStorerKey') {
        this.dataEditor.populateDropdownValuesBasedOnStorerInAccordion();
        this.dataEditor.populateDropdownValuesBasedOnStorerInTables();
      }
    }
  }

  public onValueChangeInTypeaheadTextBox(event) {
    this.valueChanged(event.propertyName, event);
  }

  // Method for checkbox starts
  public toggleSelect(key) {
    const event: PropertyChangeEvent = new PropertyChangeEvent();
    event.source = 'table-editor';
    event.type = MANUAL_EDIT;
    event.property = this.cellDataMap[key].cName;
    event.value = this.cellDataMap[key].cVal;
    const acceptChange: boolean = this.dataEditor.propertyValueChanged(event);
    this.cellDataMap[key].isEdited = true;
    if (this.cellDataMap[key].cVal === 'Y' || this.cellDataMap[key].cVal === '1') {
      // If datatype of checkbox value is string
      if (this.metadataMap[key].dataType === 'String') {
        this.cellDataMap[key].cVal = 'N';
      } else if (this.metadataMap[key].dataType === 'Integer') { // If datatype of checkbox value is string
        this.cellDataMap[key].cVal = '0';
      }
    } else if (this.cellDataMap[key].cVal === 'N' || this.cellDataMap[key].cVal === '0' || this.cellDataMap[key].cVal === null) {
      if (this.metadataMap[key].dataType === 'String') {
        this.cellDataMap[key].cVal = 'Y';
      } else if (this.metadataMap[key].dataType === 'Integer') {
        this.cellDataMap[key].cVal = '1';
      }
    }
  }

  public subscriptionFunction() {
    this.eventSubscriptions.add(this.dataService.valueChangedReceiveEvent().subscribe((event) => {
      if (event.property === 'storerKey' || event.propert === 'fromStorerKey') {
        this.dataEditor.populateDropdownValuesBasedOnStorerInAccordion();
        this.dataEditor.populateDropdownValuesBasedOnStorerInTables();
      }
    }));
  }

  public ngOnDestroy() {
    if (this.eventSubscriptions) {
      this.eventSubscriptions.unsubscribe();
    }
  }

  public toggleAccordion() {
    this.isAccordionAnimating = true;
    this.accordionConfig.isOpen = !this.accordionConfig.isOpen;
  }

}




// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/1_SCE_CI_CD_SIT/src/app/shared/components/accordion/accordion.component.ts